import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import get from 'lodash/get';
import { reduxForm } from 'redux-form';

import colors from '@app/constants/colors';
import { getAccountDetails, updateAccountData, setAccountError } from '@app/actions/accountActions';

import {
  ContainerWithLogo,
  FirstName,
  LastName,
  PhoneNumber,
  Country,
  UserName,
  LanguageSelection,
  City,
  Street,
  Zip,
  Email,
  AllowNews,
  ShareEmail,
  Button,
  Loader,
  MainContainer,
  PersonalInfo,
  AccountInfo,
  ErrorMessage,
} from '@app/components';


class MyAccount extends React.Component {
  componentDidMount() {
    const { getAccountDetails } = this.props;
    getAccountDetails();
  }

  componentWillUnmount() {
    const { setAccountError } = this.props;
    setAccountError(null);
  }

  render() {
    const { updateAccountData, isLoadingData } = this.props;

    return (
      <ContainerWithLogo titleLabel="myAccount.header">
        {
          isLoadingData ? <LoaderContainer><Loader /></LoaderContainer> : <MyAccountForm onSubmit={updateAccountData} />
        }
      </ContainerWithLogo>
    );
  }
}

MyAccount.propTypes = {
  getAccountDetails: PropTypes.func.isRequired,
  updateAccountData: PropTypes.func.isRequired,
  setAccountError: PropTypes.func.isRequired,
  isLoadingData: PropTypes.bool.isRequired,
};

export default connect(({ account: { isLoadingData } }) => ({
  isLoadingData,
}), { updateAccountData, getAccountDetails, setAccountError })(MyAccount);

const LoaderContainer = styled.div`
  margin-bottom: 20px;
  height: 100%;
`;

const PersonalInfoColors = {
  fieldColor: colors.INTERNATIONAL_ORANGE,
  textColor: colors.RUST,
};

const AccountInfoColors = {
  textColor: colors.BUTTERFLY_BUSH,
};

let MyAccountForm = ({ handleSubmit, submitting, error }) => (
  <MainContainer onSubmit={handleSubmit}>
    <PersonalInfo>
      <FieldsContainer>
        <h2><FormattedMessage id="personalInformation.personalInfoHeader" /></h2>
        <ColumnsContainer>
          <ColumnRight>
            <FirstName {...PersonalInfoColors} />
            <Country {...PersonalInfoColors} />
            <City {...PersonalInfoColors} />
            <Zip {...PersonalInfoColors} />
          </ColumnRight>
          <ColumnLeft>
            <LastName {...PersonalInfoColors} />
            <PhoneNumber {...PersonalInfoColors} />
            <Street {...PersonalInfoColors} />
          </ColumnLeft>
        </ColumnsContainer>
      </FieldsContainer>
    </PersonalInfo>
    <AccountInfo>
      <FieldsContainer>
        <h2><FormattedMessage id="personalInformation.accountInfoHeader" /></h2>
        <ColumnsContainer>
          <ColumnRight>
            <Email {...AccountInfoColors} isReadOnly />
            <LanguageSelection />
            <CheckBoxesContainer>
              <ShareEmail />
              <AllowNews />
            </CheckBoxesContainer>
          </ColumnRight>
          <ColumnLeft>
            <UserName {...AccountInfoColors} isReadOnly />
          </ColumnLeft>
        </ColumnsContainer>
        {
          error && (
            <ErrorMessage>
              <FormattedMessage id={`signUp.errors.${get(error, 'code')}`} />
            </ErrorMessage>
          )
        }
        <ButtonsContainer>
          <Button
            type="submit"
            labelId="myAccount.updateButton"
            textColor={colors.ALICE_BLUE}
            backgroundColor={colors.INTERNATIONAL_ORANGE}
            disabled={submitting}
            loading={submitting}
          />
        </ButtonsContainer>
      </FieldsContainer>
    </AccountInfo>
  </MainContainer>
);

MyAccountForm.propTypes = {
  submitting: PropTypes.bool.isRequired, // This method is added by redux-form itself,
  handleSubmit: PropTypes.func.isRequired, // This method is added by redux-form itself
  error: PropTypes.shape({
    code: PropTypes.number.isRequired,
    message: PropTypes.string,
  }),
};

MyAccountForm.defaultProps = {
  error: null,
};

MyAccountForm = connect(({ account: { error } }) => ({
  error,
}))(MyAccountForm);

MyAccountForm = reduxForm({
  form: 'signUp',
})(MyAccountForm);

/*
  Please note: it is important to call connect twice,
  because the first one is used for our component and the second one for redux-from HOC
*/

MyAccountForm = connect(({ account: { details } }) => ({
  initialValues: details,
}))(MyAccountForm);

const FieldsContainer = styled.div`
  max-width: 800px;
  width: 100%;
  margin: auto;
`;

const ColumnsContainer = styled.div`
  display: flex;
  
  @media (max-width: 720px) {
    flex-direction: column;
    align-items: center;
  }
`;

const ColumnRight = styled.div`
  width: 50%;
  margin-right: 30px;
  
  @media (max-width: 880px) {
    margin-right: 15px;
  }
  
  @media (max-width: 720px) {
    width: 90%;
    margin-right: 0;
  }
  
  @media (max-width: 400px) {
    width: 100%;
  }
`;

const ColumnLeft = styled.div`
  width: 50%;
  margin-left: 30px;
  
  @media (max-width: 880px) {
    margin-left: 15px;
  }
  
  @media (max-width: 720px) {
    width: 90%;
    margin-left: 0;
  }
  
  @media (max-width: 400px) {
   width: 100%;
  }
`;

const ButtonsContainer = styled.div`
  margin: 40px 0 0;
  text-align: center;
`;

const CheckBoxesContainer = styled.div`
  margin-top: 30px;
`;
